/**
 * Ανώτατο μήκος διεύθυνσης.
 *
 * **Αντίγραφο** του `URL_MAX` του `api/lib/schemas.js`. Η τιμή δεν μπορεί να
 * εισαχθεί: το `api/` είναι ξεχωριστό πακέτο με δικές του εξαρτήσεις. Η ταύτιση
 * επιβάλλεται από δοκιμή, το `limits.test.js`, που διαβάζει και τα τρία αρχεία.
 *
 * Ήταν 2048 ενώ το API δεχόταν 4096 (διορθώθηκε 18/08): η διεπαφή απέρριπτε
 * διεύθυνση που ο διακομιστής δεχόταν, και το μήνυμα του `el.js` έλεγε «το όριο
 * είναι 4096» — δηλαδή το ίδιο σύστημα έλεγε δύο διαφορετικά πράγματα.
 */
export const URL_MAX = 4096;

// Επικύρωση πεδίων στον πελάτη, σε **αντιστοιχία** με τα σχήματα zod του `api/`.
//
// Ο έλεγχος στον διακομιστή παραμένει η αυθεντία και δεν αντικαθίσταται: αυτός
// εδώ υπάρχει για να μη μαθαίνει ο χρήστης τον κανόνα **μετά** την υποβολή.
//
// Το εύρημα που την προκάλεσε: στην εγγραφή, κωδικός πέντε χαρακτήρων περνούσε
// τον τοπικό έλεγχο — που κοίταζε μόνο αν το πεδίο είναι κενό — και απορριπτόταν
// στην υποβολή με 422. Ο χρήστης θεωρούσε το πεδίο σωστό και ανακάλυπτε το
// αντίθετο αφού πατήσει. Παραβίαζε τη **ΜΛΑ-03**: η ανατροφοδότηση οφείλει να
// είναι έγκαιρη, όχι μόνο ορθή.
//
// Το ίδιο ίσχυε σε **τέσσερα ακόμη πεδία**, όλα με τον ίδιο μηχανισμό — τοπικός
// έλεγχος «όχι κενό» έναντι κανόνα του διακομιστή:
//
//   • τίτλος συλλογής   — ο διακομιστής θέλει 2 χαρακτήρες, ένας περνούσε τοπικά
//   • διεύθυνση πόρου   — ο διακομιστής θέλει έγκυρη URL, «abc» περνούσε τοπικά
//   • εικόνα πόρου      — ίδιο
//   • εικόνα προφίλ     — ίδιο
//   • ονοματεπώνυμο     — ο διακομιστής θέλει 2 χαρακτήρες
//
// Κάθε συνάρτηση επιστρέφει **μήνυμα ή `undefined`**, ώστε ο καλών να γράφει
// `isInvalid={Boolean(message)}` χωρίς δεύτερη λογική.

const USERNAME = /^[a-z0-9._-]+$/;
// Σκόπιμα απλή: ο έλεγχος διεύθυνσης είναι του διακομιστή. Εδώ αρκεί να πιαστεί
// το προφανές λάθος, δηλαδή κείμενο χωρίς `@` ή χωρίς τελεία στο μέρος του τομέα.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const empty = (value) => (value ?? '').trim() === '';

/** Υποχρεωτικό πεδίο, με το δικό του μήνυμα ανά περίπτωση. */
export function required(value, message) {
    return empty(value) ? message : undefined;
}

export function username(value) {
    if (empty(value)) return 'Το όνομα χρήστη είναι υποχρεωτικό.';

    const trimmed = value.trim().toLowerCase();
    if (trimmed.length < 3) return 'Τουλάχιστον 3 χαρακτήρες.';
    if (trimmed.length > 32) return 'Έως 32 χαρακτήρες.';
    if (!USERNAME.test(trimmed)) {
        return 'Μόνο λατινικά γράμματα, ψηφία, τελεία, παύλα και κάτω παύλα.';
    }
    return undefined;
}

export function email(value) {
    if (empty(value)) return 'Η διεύθυνση είναι υποχρεωτική.';
    return EMAIL.test(value.trim()) ? undefined : 'Δώστε έγκυρη διεύθυνση, π.χ. name@example.gr.';
}

/** Ο κανόνας του `passwordSchema`: 8 έως 128 χαρακτήρες. */
export function password(value) {
    if (empty(value)) return 'Ο κωδικός είναι υποχρεωτικός.';
    if (value.length < 8) return 'Τουλάχιστον 8 χαρακτήρες.';
    if (value.length > 128) return 'Έως 128 χαρακτήρες.';
    return undefined;
}

export function passwordConfirm(value, original) {
    if (empty(value)) return 'Επιβεβαιώστε τον κωδικό.';
    return value === original ? undefined : 'Οι δύο κωδικοί δεν ταιριάζουν.';
}

export function fullName(value) {
    if (empty(value)) return 'Το ονοματεπώνυμο είναι υποχρεωτικό.';
    if (value.trim().length < 2) return 'Τουλάχιστον 2 χαρακτήρες.';
    if (value.trim().length > 120) return 'Έως 120 χαρακτήρες.';
    return undefined;
}

/** Τίτλος συλλογής: 2 έως 120 χαρακτήρες. */
export function collectionTitle(value) {
    if (empty(value)) return 'Ο τίτλος είναι υποχρεωτικός.';
    if (value.trim().length < 2) return 'Τουλάχιστον 2 χαρακτήρες.';
    if (value.trim().length > 120) return 'Έως 120 χαρακτήρες.';
    return undefined;
}

/** Τίτλος πόρου: 1 έως 300 χαρακτήρες. */
export function resourceTitle(value) {
    if (empty(value)) return 'Ο τίτλος είναι υποχρεωτικός.';
    if (value.trim().length > 300) return 'Έως 300 χαρακτήρες.';
    return undefined;
}

/**
 * Απόλυτη διεύθυνση, όπως το `z.string().url()`.
 *
 * Ο έλεγχος γίνεται με τον κατασκευαστή `URL` του περιηγητή και όχι με κανονική
 * παράσταση: η δεύτερη είτε δέχεται πράγματα που δεν είναι διευθύνσεις, είτε
 * απορρίπτει έγκυρες. Το σχήμα περιορίζεται σε `http`/`https`, γιατί ένα
 * `javascript:` περνά τον κατασκευαστή.
 */
export function url(value, { requiredMessage = 'Η διεύθυνση είναι υποχρεωτική.' } = {}) {
    if (empty(value)) return requiredMessage;

    let parsed;
    try {
        parsed = new URL(value.trim());
    } catch {
        return 'Δώστε πλήρη διεύθυνση, μαζί με το https://.';
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'Επιτρέπονται μόνο διευθύνσεις http:// και https://.';
    }
    if (value.trim().length > URL_MAX) return 'Η διεύθυνση είναι υπερβολικά μεγάλη.';
    return undefined;
}

/** Προαιρετική διεύθυνση εικόνας: κενό επιτρέπεται. */
export function optionalUrl(value) {
    return empty(value) ? undefined : url(value);
}

export default {
    required, username, email, password, passwordConfirm, fullName,
    collectionTitle, resourceTitle, url, optionalUrl
};
